import { useEffect, useState } from 'react';
import {
  ACCOUNT_DELETION_STATES,
  deleteCurrentNaraAccount,
  getAccountDeletionErrorMessage,
  getCurrentAccountDeletionState,
  hasSupportedAccountDeletionProvider,
  inspectCurrentAccountDeletion,
} from '../../services/accountDeletionService';
import './styles.css';

const AccountDeletionFlow = ({ pending = false, onCancel, onSuccess }) => {
  const [state, setState] = useState(pending ? ACCOUNT_DELETION_STATES.PENDING : ACCOUNT_DELETION_STATES.IDLE);
  const [eligibility, setEligibility] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!pending) return;
    getCurrentAccountDeletionState().then(remote => setState(remote.state)).catch(nextError => {
      setState(ACCOUNT_DELETION_STATES.ERROR);
      setError(getAccountDeletionErrorMessage(nextError));
    });
  }, [pending]);

  const preflight = async () => {
    setState(ACCOUNT_DELETION_STATES.PREFLIGHT);
    setError('');
    try {
      const result = await inspectCurrentAccountDeletion();
      setEligibility(result);
      if (!result.eligible) setState(ACCOUNT_DELETION_STATES.BLOCKED);
      else if (!hasSupportedAccountDeletionProvider()) {
        setState(ACCOUNT_DELETION_STATES.ERROR);
        setError('A exclusão pela web ainda exige uma conta autenticada com email e senha. Login social não possui reautenticação compatível nesta versão.');
      } else setState(ACCOUNT_DELETION_STATES.REAUTH);
    } catch (nextError) {
      setState(ACCOUNT_DELETION_STATES.ERROR);
      setError(getAccountDeletionErrorMessage(nextError));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (confirmation !== 'EXCLUIR' || !password || state === ACCOUNT_DELETION_STATES.DELETING) return;
    setState(ACCOUNT_DELETION_STATES.DELETING);
    setError('');
    try {
      await deleteCurrentNaraAccount(password);
      setPassword('');
      setState(ACCOUNT_DELETION_STATES.SUCCESS);
      onSuccess?.();
    } catch (nextError) {
      setPassword('');
      try {
        const remote = await getCurrentAccountDeletionState();
        setState(remote.pending ? ACCOUNT_DELETION_STATES.PENDING : ACCOUNT_DELETION_STATES.ERROR);
      } catch (_) { setState(ACCOUNT_DELETION_STATES.PENDING); }
      setError(getAccountDeletionErrorMessage(nextError));
    }
  };

  if (state === ACCOUNT_DELETION_STATES.SUCCESS) return <div className="deletion-result" role="status"><h2>Conta excluída</h2><p>A exclusão definitiva foi concluída.</p></div>;

  return <section className="deletion-flow" aria-live="polite">
    <h2>Exclusão permanente</h2>
    <p>Este processo remove sua identidade Nara, perfil, contas exclusivas e seus dados associados. Dados de contas pertencentes a outras pessoas não são apagados: somente seu vínculo é removido.</p>
    <p><strong>A operação é irreversível.</strong></p>
    {state === ACCOUNT_DELETION_STATES.PENDING && <div className="auth-alert auth-alert--error" role="alert">Uma exclusão anterior não terminou. O uso financeiro está bloqueado até a retomada segura.</div>}
    {state === ACCOUNT_DELETION_STATES.BLOCKED && <div className="auth-alert auth-alert--error" role="alert">
      Você ainda é proprietário de {eligibility?.blockedAccounts?.length || 'uma'} conta(s) com outros membros. Remova os demais membros ou resolva a propriedade antes de continuar.
    </div>}
    {error && <div className="auth-alert auth-alert--error" role="alert">{error}</div>}

    {[ACCOUNT_DELETION_STATES.IDLE, ACCOUNT_DELETION_STATES.ERROR, ACCOUNT_DELETION_STATES.PENDING].includes(state) && <button className="auth-primary-button deletion-danger" type="button" onClick={preflight}>
      {state === ACCOUNT_DELETION_STATES.PENDING ? 'Retomar exclusão' : 'Excluir minha conta'}
    </button>}
    {state === ACCOUNT_DELETION_STATES.PREFLIGHT && <p role="status">Verificando contas e vínculos…</p>}
    {state === ACCOUNT_DELETION_STATES.REAUTH && <form className="auth-form" onSubmit={submit}>
      <label className="nf-field"><span>Digite EXCLUIR para confirmar</span><input value={confirmation} onChange={event => setConfirmation(event.target.value)} autoComplete="off" /></label>
      <label className="nf-field"><span>Senha atual</span><input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" /></label>
      <button className="auth-primary-button deletion-danger" disabled={confirmation !== 'EXCLUIR' || !password} type="submit">Excluir permanentemente</button>
    </form>}
    {state === ACCOUNT_DELETION_STATES.DELETING && <p role="status">Excluindo com segurança. Não feche esta página…</p>}
    {onCancel && state !== ACCOUNT_DELETION_STATES.DELETING && <button className="deletion-cancel" type="button" onClick={onCancel}>Cancelar</button>}
  </section>;
};

export default AccountDeletionFlow;
